import { createFeatureSelector, createSelector } from '@ngrx/store';

import { PlanCheckStatus } from '@core/models/plan-finding.model';
import { occurrenceCounts } from '@core/services/gbe-error-parser/occurrence-counts';
import { selectPlanWatch } from '@core/store/plan-edit/plan-edit.selectors';
import { watchIsLiveFor } from '@core/store/plan-edit/plan-edit.state';
import { selectPlan } from '@core/store/plan/plan.selectors';
import { isNil } from '@core/utils/shared-utils';

import { PlanFindingsState } from './plan-findings.state';

export const selectPlanFindingsState =
  createFeatureSelector<PlanFindingsState>('planFindings');

const selectFindingsPlanId = createSelector(selectPlanFindingsState, (s) => s.planId);
const selectFindingsParamId = createSelector(selectPlanFindingsState, (s) => s.paramId);

/**
 * The plan and parameter set a check runs for; null until the plan's context is announced. Built
 * from the two ids alone so it keeps its identity across every read and check of that context.
 */
export const selectFindingsCheckContext = createSelector(
  selectFindingsPlanId,
  selectFindingsParamId,
  (planId, paramId) => (isNil(planId) || isNil(paramId) ? null : { planId, paramId })
);

export const selectFindings = createSelector(
  selectPlanFindingsState,
  (state) => state.findings
);

const selectLiveFindings = createSelector(selectPlanWatch, selectPlan, (watch, plan) =>
  watchIsLiveFor(watch, plan?.id ?? null) ? watch.findings : null
);

/**
 * Live session findings while the watch is live for the plan in view, else the stored check:
 * the stored check describes the plan as saved, not the session's edits.
 */
export const selectPlanFindingsInView = createSelector(
  selectLiveFindings,
  selectFindings,
  (live, stored) => live ?? stored
);

export const selectPlanFindingsLive = createSelector(
  selectLiveFindings,
  (live) => live !== null
);

export const selectFindingsStatus = createSelector(
  selectPlanFindingsState,
  (state) => state.status
);

export const selectFindingsAreStale = createSelector(
  selectPlanFindingsState,
  (state) => state.isStale
);

export const selectFindingsPhase = createSelector(
  selectPlanFindingsState,
  (state) => state.phase
);

export const selectFindingsEditSeq = createSelector(
  selectPlanFindingsState,
  (state) => state.editSeq
);

export const selectBlockingFindings = createSelector(selectFindings, (findings) =>
  findings.filter((finding) => finding.blocksOptimization)
);

export const selectOptimizeBlocked = createSelector(
  selectBlockingFindings,
  (blocking) => blocking.length > 0
);

const selectFindingsHaveResolved = createSelector(
  selectPlanFindingsState,
  (state) => state.hasResolved
);

/** The plan has no parameter set to be checked against. */
export const selectFindingsUnavailable = createSelector(
  selectFindingsPhase,
  (phase) => phase === 'UNAVAILABLE'
);

/**
 * Gated on a server answer: `NOT_CHECKED` is also the value the slice starts with, so without
 * the gate every plan reads as unchecked for as long as its findings are in flight, and a plan
 * that cannot be checked at all would read as one nobody has checked yet.
 */
export const selectPlanNeverChecked = createSelector(
  selectFindingsHaveResolved,
  selectFindingsUnavailable,
  selectFindingsStatus,
  (hasResolved, unavailable, status) =>
    hasResolved && !unavailable && status === PlanCheckStatus.NOT_CHECKED
);

/**
 * Work is in flight and the plan's status is not yet known — either the first read has not
 * come back, or a check is running, which is what an unchecked plan triggers automatically.
 */
export const selectFindingsBusy = createSelector(
  selectFindingsHaveResolved,
  selectFindingsPhase,
  (hasResolved, phase) => !hasResolved || phase === 'CHECKING'
);

export type PlanFindingsStatusKind =
  | 'busy'
  | 'unavailable'
  | 'never_checked'
  | 'blocking'
  | 'warnings'
  | 'stale'
  | 'ok';

export interface PlanFindingsStatus {
  kind: PlanFindingsStatusKind;
  /** Blocking occurrences for `blocking`, all occurrences for `warnings`, else 0. */
  count: number;
  stale: boolean;
}

/**
 * The one-line status of the findings in view. Busy, unavailable, never-checked and stale describe the
 * stored check, so they are ignored while the findings in view are live: they would describe
 * the plan as saved, not the session's edits. A status the page does not know yet is never
 * stated, which is why busy outranks everything.
 */
export const selectPlanFindingsStatusInView = createSelector(
  selectPlanFindingsLive,
  selectPlanFindingsInView,
  selectFindingsBusy,
  selectFindingsUnavailable,
  selectPlanNeverChecked,
  selectFindingsAreStale,
  (live, findings, busy, unavailable, neverChecked, isStale): PlanFindingsStatus => {
    const stale = !live && isStale;
    if (!live && busy) return { kind: 'busy', count: 0, stale };
    if (!live && unavailable) return { kind: 'unavailable', count: 0, stale: false };
    if (!live && neverChecked) return { kind: 'never_checked', count: 0, stale };
    const { total, blocking } = occurrenceCounts(findings);
    if (blocking > 0) return { kind: 'blocking', count: blocking, stale };
    if (total > 0) return { kind: 'warnings', count: total, stale };
    // A clean check of a plan that changed since must not read as OK.
    return { kind: stale ? 'stale' : 'ok', count: 0, stale };
  }
);
